import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, FormField, Modal } from '@noor/ui';
import type { ModalRenderProps } from '@noor/lib';
import { Can } from '@/entities/access';
import {
  cancelFoodOrderSchema,
  createTestFoodOrderSchema,
  type CancelFoodOrder,
  type CreateTestFoodOrder,
} from '@/entities/food-order';
import {
  useCancelFoodOrder,
  useCreateTestFoodOrder,
} from '../model/use-food-order-actions';

export function CancelFoodOrderModal({
  payload,
  close,
}: ModalRenderProps<'cancel-food-order'>) {
  const mutation = useCancelFoodOrder(payload.orderId);
  const form = useForm<CancelFoodOrder>({
    resolver: zodResolver(cancelFoodOrderSchema),
    defaultValues: { reason: '' },
  });
  return (
    <Can permission="food_orders.cancel">
      <Modal
        title={`Отменить заказ № ${payload.orderNumber ?? payload.orderId}?`}
        description="Причина будет сохранена в заказе и показана ресторану."
        dismissible={!mutation.isPending}
        footer={
          <>
            <Button
              variant="soft"
              shape="rounded"
              size="modal"
              onClick={close}
              disabled={mutation.isPending}
            >
              Назад
            </Button>
            <Button
              variant="destructive"
              type="submit"
              form="cancel-food-order"
              shape="rounded"
              size="modal"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Отмена…' : 'Подтвердить отмену'}
            </Button>
          </>
        }
      >
        <FormProvider {...form}>
          <form
            id="cancel-food-order"
            onSubmit={form.handleSubmit((values) =>
              mutation.mutate(values, { onSuccess: close }),
            )}
          >
            <fieldset disabled={mutation.isPending}>
              <FormField name="reason">
                <FormField.Label>Причина отмены</FormField.Label>
                <FormField.TextArea
                  rows={4}
                  placeholder="Укажите причину для ресторана"
                />
                <FormField.Message />
              </FormField>
            </fieldset>
          </form>
        </FormProvider>
      </Modal>
    </Can>
  );
}
export function CreateTestFoodOrderModal({
  close,
}: ModalRenderProps<'create-test-food-order'>) {
  const mutation = useCreateTestFoodOrder();
  const form = useForm<CreateTestFoodOrder>({
    resolver: zodResolver(createTestFoodOrderSchema),
    defaultValues: {
      restaurant_id: '',
      product_id: '',
      quantity: 1,
      delivery_method: 'DELIVERY_METHOD_DELIVERY',
    },
  });
  return (
    <Can permission="food_orders.create_test">
      <Modal
        title="Создать тестовый заказ"
        description="Будет создан заказ в выбранном ресторане."
        dismissible={!mutation.isPending}
        footer={
          <>
            <Button
              variant="soft"
              shape="rounded"
              size="modal"
              onClick={close}
              disabled={mutation.isPending}
            >
              Отмена
            </Button>
            <Button
              type="submit"
              form="create-test-food-order"
              variant="dark"
              shape="rounded"
              size="modal"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Создание…' : 'Создать'}
            </Button>
          </>
        }
      >
        <FormProvider {...form}>
          <form
            id="create-test-food-order"
            onSubmit={form.handleSubmit((values) =>
              mutation.mutate(values, { onSuccess: close }),
            )}
          >
            <fieldset disabled={mutation.isPending} className="space-y-4">
              <FormField name="restaurant_id">
                <FormField.Label>ID ресторана</FormField.Label>
                <FormField.Input placeholder="UUID ресторана" />
                <FormField.Message />
              </FormField>
              <FormField name="product_id">
                <FormField.Label>ID продукта (необязательно)</FormField.Label>
                <FormField.Input placeholder="По умолчанию — популярный продукт" />
                <FormField.Message />
              </FormField>
              <FormField name="quantity">
                <FormField.Label>Количество</FormField.Label>
                <FormField.Number min={1} />
                <FormField.Message />
              </FormField>
              <FormField name="delivery_method">
                <FormField.Label>Способ получения</FormField.Label>
                <FormField.Select
                  options={[
                    { value: 'DELIVERY_METHOD_DELIVERY', label: 'Доставка' },
                    { value: 'DELIVERY_METHOD_PICKUP', label: 'Самовывоз' },
                  ]}
                />
                <FormField.Message />
              </FormField>
            </fieldset>
          </form>
        </FormProvider>
      </Modal>
    </Can>
  );
}
